import React, { useState, useRef, createElement, Component } from 'react';
import { useClickOutside } from '../../../../app/hooks/useClickOutside';
import { AlertTriangle, XCircle, CheckCircle2, Info, Search, ChevronDown, ChevronRight, Download, Hash, ShieldAlert } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import type { PipelineBatchResult, GateVerdict, ValidationIssue, IssueDomain } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
// ─── Constants ────────────────────────────────────────────────────────────────
export const MODULE_CONFIG = ADMIN_MODULE_META;
export const PIPELINE_STAGES: {
  name: string;
  label: string;
  domain: IssueDomain;
}[] = [{
  name: 'schemaStage',
  label: 'Schema Validatie',
  domain: 'schema'
}, {
  name: 'typeStage',
  label: 'Vraagtype Validatie',
  domain: 'type'
}, {
  name: 'contentStage',
  label: 'Inhoud & Taal',
  domain: 'content'
}, {
  name: 'pedagogyStage',
  label: 'Pedagogisch Contract',
  domain: 'pedagogy'
}, {
  name: 'supportStage',
  label: 'Hints & Uitleg',
  domain: 'support'
}, {
  name: 'visualStage',
  label: 'Visuele Hulpmiddelen',
  domain: 'visual'
}, {
  name: 'graphStage',
  label: 'LDG-Graaf',
  domain: 'graph'
}, {
  name: 'seedStage',
  label: 'Seed Integriteit',
  domain: 'seed'
}];
export const SEVERITY_CONFIG = {
  blocker: {
    icon: XCircle,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blocker'
  },
  coach: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Verbeterpunt'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Info'
  }
};
// ─── Types ────────────────────────────────────────────────────────────────────
export interface LogEntry {
  time: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}
// ─── Shared Components ────────────────────────────────────────────────────────
export function EmptyValidationState() {
  return <div className="py-16 flex flex-col items-center justify-center space-y-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-4132">
      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100" data-id="element-4133">
        <ShieldAlert className="w-8 h-8 text-gray-300" data-id="element-4134" />
      </div>
      <div className="text-center" data-id="element-4135">
        <h2 className="text-base font-bold text-gray-900" data-id="element-4136">
          Nog geen validatie uitgevoerd
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto" data-id="element-4137">
          Klik op 'Valideer Nu' om de governance pipeline te starten en het
          vragenbestand te controleren.
        </p>
      </div>
    </div>;
}
export function FilterBar({
  moduleFilter,
  onModuleFilter,
  search,
  onSearch,
  children
}: {
  moduleFilter: string;
  onModuleFilter: (v: string) => void;
  search: string;
  onSearch: (v: string) => void;
  children?: React.ReactNode;
}) {
  const moduleOptions = [{
    value: 'all',
    label: 'Alle domeinen'
  }, ...Object.entries(MODULE_CONFIG).map(([code, config]) => ({
    value: code,
    label: `${config.name} (${code})`
  }))];
  return <div className="flex flex-col sm:flex-row gap-3 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-4138">
      <div className="flex-1 w-full sm:max-w-sm" data-id="element-4139">
        <Input type="text" placeholder="Zoek op ID of code..." value={search} onChange={e => onSearch(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4141" />} variant="admin" size="sm" data-id="element-4140" />
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-4142">
        <Select value={moduleFilter} onChange={e => onModuleFilter(e.target.value)} options={moduleOptions} variant="admin" size="sm" fullWidth={false} data-id="element-4143" />
        {children}
      </div>
    </div>;
}
export function IssueRow({
  issue
}: {
  issue: ValidationIssue;
}) {
  const sevConfig = SEVERITY_CONFIG[issue.kind];
  const Icon = sevConfig.icon;
  return <div className={`flex items-start gap-3 p-3 rounded-lg border ${sevConfig.bg} ${sevConfig.border}`} data-id="element-4144">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sevConfig.text}`} data-id="element-4145" />
      <div className="min-w-0 flex-1" data-id="element-4146">
        <div className="flex items-center gap-2 mb-1" data-id="element-4147">
          <span className={`text-xs font-bold uppercase ${sevConfig.text}`} data-id="element-4148">
            {sevConfig.label}
          </span>
          <span className="text-[10px] font-mono text-gray-500 bg-white/50 px-1.5 py-0.5 rounded border border-gray-200/50" data-id="element-4149">
            {issue.code}
          </span>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-4150">
            {issue.domain}
          </span>
        </div>
        <p className="text-sm text-gray-800 leading-snug" data-id="element-4151">{issue.message}</p>
        {issue.suggestion && <p className="text-xs text-gray-600 mt-1.5 flex items-start gap-1.5" data-id="element-4152">
            <span className="font-medium text-gray-700" data-id="element-4153">Suggestie:</span>
            {issue.suggestion}
          </p>}
      </div>
    </div>;
}
export function QuestionResultRow({
  questionId,
  verdict,
  score,
  issues,
  isExpanded,
  onToggle
}: {
  questionId: string;
  verdict: GateVerdict;
  score: number;
  issues: ValidationIssue[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const prefix = questionId.split('-')[0];
  const modConfig = MODULE_CONFIG[prefix] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  const statusConfig = {
    PASS: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: CheckCircle2,
      label: 'Geslaagd'
    },
    REVIEW_NEEDED: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: AlertTriangle,
      label: 'Review'
    },
    FAIL: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: XCircle,
      label: 'Fail'
    }
  }[verdict];
  const StatusIcon = statusConfig.icon;
  return <Card className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4154">
      <div className="px-4 py-3 flex items-center justify-between cursor-pointer bg-white" onClick={onToggle} data-id="element-4155">
        <div className="flex items-center gap-3 min-w-0" data-id="element-4156">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4157" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0" data-id="element-4158">
            <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-4159">
              {questionId}
            </span>
            <div className="flex items-center gap-2" data-id="element-4160">
              <Badge variant={verdict === 'PASS' ? 'success' : verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-4161">
                <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-4162" />
                {statusConfig.label}
              </Badge>
              {issues.length > 0 && <span className="text-xs text-gray-500" data-id="element-4163">
                  {issues.length} issue{issues.length > 1 ? 's' : ''}
                </span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-4164">
          <div className="hidden sm:flex items-center gap-2" data-id="element-4165">
            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4166">
              <div className={`h-full rounded-full ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
              width: `${score}%`
            }} data-id="element-4167" />
            </div>
            <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-4168">
              {score}%
            </span>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-4169" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-4170" />}
        </div>
      </div>
      {isExpanded && issues.length > 0 && <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2" data-id="element-4171">
          {issues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4172" />)}
        </div>}
      {isExpanded && issues.length === 0 && <div className="px-4 py-6 bg-gray-50 border-t border-gray-100 text-center" data-id="element-4173">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" data-id="element-4174" />
          <p className="text-sm text-gray-600" data-id="element-4175">
            Geen issues gevonden. Deze vraag voldoet aan alle kwaliteitseisen.
          </p>
        </div>}
    </Card>;
}
// ─── Export Utilities ─────────────────────────────────────────────────────────
/** Priority score for sorting: higher = more urgent to fix */
function computePriority(r: PipelineBatchResult['results'][0]): number {
  const blockers = r.gate.issues.filter(i => i.kind === 'blocker').length;
  const coaches = r.gate.issues.filter(i => i.kind === 'coach').length;
  const maxSeverity = r.gate.issues.reduce((max, i) => Math.max(max, i.severity), 0);
  // Blockers weigh heaviest, then coaches, then severity, then inverse score
  return blockers * 1000 + coaches * 100 + maxSeverity + (100 - r.gate.score);
}
/** Sort results by priority: most problematic first */
function sortByPriority(results: PipelineBatchResult['results']): PipelineBatchResult['results'] {
  return [...results].sort((a, b) => computePriority(b) - computePriority(a));
}
/** Filter to only questions with blockers or multiple warnings */
function filterPriorityOnly(results: PipelineBatchResult['results']): PipelineBatchResult['results'] {
  return results.filter(r => {
    const blockers = r.gate.issues.filter(i => i.kind === 'blocker').length;
    const coaches = r.gate.issues.filter(i => i.kind === 'coach').length;
    return blockers > 0 || coaches >= 2;
  });
}
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function exportCSV(results: PipelineBatchResult['results'], filename: string, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const rows = [['Priority', 'Question ID', 'Family ID', 'Verdict', 'Score', 'Blockers', 'Warnings', 'Info', 'Total Issues', 'Domains', 'Top Issue Codes', 'Max Severity']];
  sorted.forEach((r, idx) => {
    const blockers = r.gate.issues.filter(i => i.kind === 'blocker');
    const coaches = r.gate.issues.filter(i => i.kind === 'coach');
    const infos = r.gate.issues.filter(i => i.kind === 'info');
    const domains = [...new Set(r.gate.issues.map(i => i.domain))].join('; ');
    const topCodes = r.gate.issues.sort((a, b) => b.severity - a.severity).slice(0, 3).map(i => i.code).join('; ');
    const maxSev = r.gate.issues.reduce((max, i) => Math.max(max, i.severity), 0);
    rows.push([String(idx + 1), r.questionId, r.familyId, r.gate.verdict, r.gate.score.toString(), blockers.length.toString(), coaches.length.toString(), infos.length.toString(), r.gate.issues.length.toString(), domains, topCodes, maxSev.toString()]);
  });
  // Escape CSV fields that might contain commas/semicolons
  const csv = rows.map(r => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadBlob(new Blob([csv], {
    type: 'text/csv'
  }), `${filename}.csv`);
}
export function exportJSON(results: PipelineBatchResult['results'], filename: string, meta?: Record<string, unknown>, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const data = {
    timestamp: new Date().toISOString(),
    exportType: priorityOnly ? 'priority' : 'full',
    totalExported: sorted.length,
    totalInPipeline: results.length,
    meta,
    summary: {
      blockerQuestions: sorted.filter(r => r.gate.issues.some(i => i.kind === 'blocker')).length,
      reviewQuestions: sorted.filter(r => r.gate.verdict === 'REVIEW_NEEDED').length,
      passQuestions: sorted.filter(r => r.gate.verdict === 'PASS').length
    },
    results: sorted.map((r, idx) => ({
      priority: idx + 1,
      priorityScore: computePriority(r),
      questionId: r.questionId,
      familyId: r.familyId,
      verdict: r.gate.verdict,
      score: r.gate.score,
      issues: r.gate.issues.sort((a, b) => b.severity - a.severity).map(i => ({
        code: i.code,
        kind: i.kind,
        domain: i.domain,
        severity: i.severity,
        message: i.message,
        suggestion: i.suggestion
      }))
    }))
  };
  downloadBlob(new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  }), `${filename}.json`);
}
export function exportMarkdown(results: PipelineBatchResult['results'], filename: string, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const fails = sorted.filter(r => r.gate.verdict === 'FAIL');
  const reviews = sorted.filter(r => r.gate.verdict === 'REVIEW_NEEDED');
  const passes = sorted.filter(r => r.gate.verdict === 'PASS');
  let md = `# Governance Validatie Rapport${priorityOnly ? ' — Prioriteit' : ''}\n\n`;
  md += `Gegenereerd op: ${new Date().toLocaleString('nl-NL')}\n`;
  if (priorityOnly) {
    md += `\n> **Prioriteitsexport:** Alleen vragen met blockers of ≥2 verbeterpunten.\n`;
    md += `> ${sorted.length} van ${results.length} vragen geëxporteerd.\n`;
  }
  md += `\n## Samenvatting\n\n`;
  md += `| Status | Aantal |\n|---|---|\n`;
  md += `| 🔴 FAIL | ${fails.length} |\n`;
  md += `| 🟡 REVIEW | ${reviews.length} |\n`;
  md += `| 🟢 PASS | ${passes.length} |\n`;
  md += `| **Totaal** | **${sorted.length}** |\n\n`;
  // Domain breakdown
  const domainCounts: Record<string, number> = {};
  sorted.forEach(r => r.gate.issues.forEach(i => {
    domainCounts[i.domain] = (domainCounts[i.domain] ?? 0) + 1;
  }));
  if (Object.keys(domainCounts).length > 0) {
    md += `## Issues per Domein\n\n`;
    md += `| Domein | Aantal |\n|---|---|\n`;
    Object.entries(domainCounts).sort((a, b) => b[1] - a[1]).forEach(([domain, count]) => {
      md += `| ${domain} | ${count} |\n`;
    });
    md += '\n';
  }
  if (fails.length > 0) {
    md += `## 🔴 Geblokkeerde Vragen (FAIL)\n\n`;
    fails.forEach((r, idx) => {
      md += `### ${idx + 1}. \`${r.questionId}\` — Score: ${r.gate.score}%\n\n`;
      const issuesSorted = [...r.gate.issues].sort((a, b) => b.severity - a.severity);
      issuesSorted.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** \`${i.code}\` (severity ${i.severity})\n  ${i.message}\n`;
        if (i.suggestion) md += `  → _${i.suggestion}_\n`;
      });
      md += '\n';
    });
  }
  if (reviews.length > 0) {
    md += `## 🟡 Aandacht Vereist (REVIEW)\n\n`;
    reviews.forEach((r, idx) => {
      md += `### ${idx + 1}. \`${r.questionId}\` — Score: ${r.gate.score}%\n\n`;
      const issuesSorted = [...r.gate.issues].sort((a, b) => b.severity - a.severity);
      issuesSorted.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** \`${i.code}\` (severity ${i.severity})\n  ${i.message}\n`;
        if (i.suggestion) md += `  → _${i.suggestion}_\n`;
      });
      md += '\n';
    });
  }
  downloadBlob(new Blob([md], {
    type: 'text/markdown'
  }), `${filename}.md`);
}
export function ExportMenu({
  results,
  filePrefix,
  meta
}: {
  results: PipelineBatchResult['results'];
  filePrefix: string;
  meta?: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setOpen(false));
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${filePrefix}-${dateStr}`;
  const priorityCount = filterPriorityOnly(results).length;
  return <div className="relative" ref={menuRef} data-id="element-4176">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4177">
        <Download className="w-4 h-4" data-id="element-4178" />
        <span className="hidden sm:inline" data-id="element-4179">Exporteer</span>
        <ChevronDown className="w-3.5 h-3.5 ml-0.5 text-gray-400" data-id="element-4180" />
      </button>
      {open && <div className="absolute right-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-4181">
          {/* Priority exports */}
          <div className="px-3 py-1.5 border-b border-gray-100" data-id="element-4182">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-600" data-id="element-4183">
              Prioriteit ({priorityCount} vragen)
            </p>
          </div>
          <button onClick={() => {
        exportCSV(results, `${filename}-prioriteit`, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4184">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4185" />
            Prioriteit CSV
          </button>
          <button onClick={() => {
        exportJSON(results, `${filename}-prioriteit`, meta, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4186">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4187" />
            Prioriteit JSON
          </button>
          <button onClick={() => {
        exportMarkdown(results, `${filename}-prioriteit`, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4188">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4189" />
            Prioriteit Markdown
          </button>

          {/* Full exports */}
          <div className="px-3 py-1.5 border-b border-t border-gray-100 mt-1" data-id="element-4190">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-4191">
              Volledig ({results.length} vragen)
            </p>
          </div>
          <button onClick={() => {
        exportCSV(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4192">
            Exporteer als CSV
          </button>
          <button onClick={() => {
        exportJSON(results, filename, meta);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4193">
            Exporteer als JSON
          </button>
          <button onClick={() => {
        exportMarkdown(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4194">
            Exporteer als Markdown
          </button>
        </div>}
    </div>;
}